import { DiscountSettingType, PaymentSettingType } from '@/models/sales'
import { AccountsReceivableSettingType } from '@/models/accounts-receivable'
import { AdvanceCompanyType } from '@/models/sales-manager/advance-money'

export const SalesCategory = {
  Customer: 'customer',
  Temporary: 'temporary',
  Other: 'other',
} as const
export type SalesCategory = (typeof SalesCategory)[keyof typeof SalesCategory]

export const SalesPriceSeparate = {
  Simple: '0',
  AdultsAndChildren: '3',
  Adults: '4',
  Children: '5',
  AdultsMale: '6',
  AdultsFemale: '7',
  ChildA: '8',
  ChildB: '9',
  ChildC: '10',
  ChildD: '11',
  ChildE: '24',
  ChildF: '25',
  ChildOther: '12',
} as const
export type SalesPriceSeparate = (typeof SalesPriceSeparate)[keyof typeof SalesPriceSeparate]
export const SalesPriceOutsidePlanSeparate = {
  Simple: '99',
  AdultsAndChildren: '13',
  Adults: '14',
  Children: '15',
  AdultsMale: '16',
  AdultsFemale: '17',
  ChildA: '18',
  ChildB: '19',
  ChildC: '20',
  ChildD: '21',
  ChildE: '26',
  ChildF: '27',
  ChildOther: '22',
} as const
export type SalesPriceOutsidePlanSeparate = (typeof SalesPriceOutsidePlanSeparate)[keyof typeof SalesPriceOutsidePlanSeparate]

type SalesSubjectSetMapType = {
  salesSubjectMasterId: string
  salesSubjectMasterName: string
  salesSubSubjectId: string | undefined
  salesSubSubjectName: string | undefined
}

export type SalesSubjectSetType = {
  salesSubjectSetId: string
  hotelId: string
  salesSubjectSetNameId: number
  salesSubjectSetName: string
  salesSubjectSetMap: SalesSubjectSetMapType[]
}

export type SalesDepartmentMasterType = {
  id: string
  departmentCode: string
  departmentName: string
  relatedSubjectMasterCount: number
}
export type SalesSubjectMasterType = {
  id: string
  subjectCode: string
  subjectName: string
  departmentId: string
  departmentCode: string
  departmentName: string
  category: SalesCategory | undefined
  separated: SalesPriceSeparate | SalesPriceOutsidePlanSeparate | undefined
  price: number
  tax: number
  planCodes: string[]
  displayOrder: number
  isSubSubjectOnly: boolean
  subSubjects: SalesSubSubjectMasterType[]
}
export type SalesSubSubjectMasterType = {
  id: string
  defaultMasterId: string
  salesSubjectMasterId: string
  subSubjectCode: string
  subSubjectName: string
  price: number
  displayOrder: number
}

export const ChildrenClass = { A: 'a', B: 'b', C: 'c', D: 'd', E: 'e', F: 'f', Other: 'o' } as const
export type ChildrenClass = (typeof ChildrenClass)[keyof typeof ChildrenClass]

/**
 * 売上明細検索パラメータの型定義
 */
export type SalesDetailSearchType = {
  // 利用明細日付（既存）
  beginDate?: string
  endDate?: string
  // 未収金 (空=すべて, 1=あり, 0=なし)
  unpaidType?: '' | '1' | '0'
  // 科目（科目のみ選択時）
  salesSubjectMasterId?: string
  // 補助科目（補助科目選択時はこちらで送る）
  salesSubSubjectId?: string
  // 割引科目名
  salesDiscountId?: string
  discountSubjectName?: string
  // 売上金額
  minSalesPrice?: number
  maxSalesPrice?: number
  // 支払い方法
  salesPaymentId?: string
  paymentMethodName?: string
  // 売掛先
  salesAccountsReceivableMasterId?: string
  accountsReceivableName?: string
  // 入金日時
  beginPaymentDate?: string
  beginPaymentHour?: string
  beginPaymentMinute?: string
  endPaymentDate?: string
  endPaymentHour?: string
  endPaymentMinute?: string
}
export const SalesSubjectTaxPriceType = { FixAmount: 'fixAmount', FixRate: 'fixRate' } as const
export type SalesSubjectTaxPriceType = (typeof SalesSubjectTaxPriceType)[keyof typeof SalesSubjectTaxPriceType]

export const SalesSubjectTaxRoundingUnit = { Hundred: 100, Thousand: 1000 } as const
export type SalesSubjectTaxRoundingUnit = (typeof SalesSubjectTaxRoundingUnit)[keyof typeof SalesSubjectTaxRoundingUnit]

export const SalesSubjectTaxRoundingType = { RoundDown: 'roundDown', RoundUp: 'roundUp' } as const
export type SalesSubjectTaxRoundingType = (typeof SalesSubjectTaxRoundingType)[keyof typeof SalesSubjectTaxRoundingType]

type BasicSalesSubjectTaxType = {
  id: string
  name: string
  childrenClass: ChildrenClass[]
  isIncludeDayUse: boolean
  isIncludeInReservationPrice: boolean
  validFrom: string | null
  validTo: string | null
  salesSubject: {
    salesDepartmentId: string
    salesDepartmentCode: string
    salesDepartmentName: string
    salesSubjectMasterId: string
    salesSubjectMasterCode: string
    salesSubjectMasterName: string
    salesSubSubjectCode: string
    salesSubSubjectName: string
  }
}
type SalesSubjectTaxFixAmountType = BasicSalesSubjectTaxType & {
  priceType: typeof SalesSubjectTaxPriceType.FixAmount
  roomChargeRange: Array<{
    upperPrice: number
    lowerPrice: number
    taxPrice: number
  }>
}
type SalesSubjectTaxFixRateType = BasicSalesSubjectTaxType & {
  priceType: typeof SalesSubjectTaxPriceType.FixRate
  roomChargeRate: number
  roomChargeRoundingUnit: SalesSubjectTaxRoundingUnit
  roomChargeRoundingType: SalesSubjectTaxRoundingType
  roomChargeUpperLimit: number | null
  roomChargeLowerLimit: number | null
}
export type SalesSubjectTaxType = SalesSubjectTaxFixAmountType | SalesSubjectTaxFixRateType

/**
 * 予約詳細画面の売上マスタ一括取得（GET /sales-manager/reservation-detail-masters）のレスポンス。
 * 各項目は個別エンドポイント（sales-subject, sales-payment など）と同じ形。
 */
export type ReservationDetailSalesMastersType = {
  salesSubjects: SalesSubjectMasterType[]
  salesPayments: PaymentSettingType[]
  salesDiscounts: Required<DiscountSettingType>[]
  accountsReceivables: AccountsReceivableSettingType[]
  advanceCompany: AdvanceCompanyType
  salesSubjectSets: SalesSubjectSetType[]
  salesSubjectTaxes: SalesSubjectTaxType[]
}
